import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, Chip, Grid, CircularProgress, FormControlLabel, Checkbox
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';

// Grouped without a currency prefix — these tables carry the currency in their heading.
const fmt = (n) => formatAmount(n);
const asDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const BLANK = {
  name: '', description: '', acquisitionDate: new Date().toISOString().slice(0, 10),
  cost: '', residualValue: 0, method: 'straight_line', usefulLifeMonths: 48,
  annualRatePercent: 30, postAcquisition: false, paymentModeId: '', paidFromAccountId: ''
};

const STATUS_COLOUR = { active: 'success', fully_depreciated: 'default', disposed: 'warning' };
const STATUS_LABEL = { active: 'Active', fully_depreciated: 'Fully depreciated', disposed: 'Disposed' };

const FixedAssets = () => {
  const [data, setData] = useState({ assets: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [modes, setModes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [disposing, setDisposing] = useState(null);
  const [disposal, setDisposal] = useState({ disposalDate: '', proceeds: 0, proceedsAccountId: '', notes: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [assets, modeRes, accRes] = await Promise.all([
        axios.get('/fixed-assets'),
        axios.get('/payment-modes').catch(() => ({ data: [] })),
        axios.get('/accounting/accounts').catch(() => ({ data: [] }))
      ]);
      setData(assets.data);
      setModes(Array.isArray(modeRes.data) ? modeRes.data : modeRes.data?.modes || []);
      setAccounts(accRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load the asset register');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only accounts that hold money can have paid for an asset — the same rule payments and
  // expenses use when choosing where money came from.
  const moneyAccounts = accounts.filter((a) => a.type === 'asset' && a.accountCategory);
  const selectedMode = modes.find((m) => m._id === form.paymentModeId || m.id === form.paymentModeId);
  const eligibleAccounts = selectedMode
    ? moneyAccounts.filter((a) => a.accountCategory === selectedMode.accountCategory)
    : moneyAccounts;

  const save = async () => {
    try {
      setWorking(true);
      await axios.post('/fixed-assets', form);
      toast.success('Asset registered');
      setShowForm(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to register the asset');
    } finally {
      setWorking(false);
    }
  };

  const runDepreciation = async () => {
    try {
      setWorking(true);
      const res = await axios.post('/fixed-assets/run-depreciation', {});
      toast.success(res.data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Depreciation run failed');
    } finally {
      setWorking(false);
    }
  };

  const dispose = async () => {
    try {
      setWorking(true);
      const res = await axios.post(`/fixed-assets/${disposing._id}/dispose`, disposal);
      toast.success(res.data.message);
      setDisposing(null);
      setDisposal({ disposalDate: '', proceeds: 0, proceedsAccountId: '', notes: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to dispose of the asset');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        The agency's own assets — vehicles, computers, office equipment. Landlords' buildings
        do not belong here: the agency manages them but does not own them, so they are not on
        the agency's balance sheet.
      </Alert>

      <Box mb={2} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => { setForm(BLANK); setShowForm(true); }}>
          Register Asset
        </Button>
        <Button variant="outlined" onClick={runDepreciation} disabled={working}>
          Run Monthly Depreciation
        </Button>
      </Box>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Cost</Typography>
            <Typography variant="h6">{fmt(data.totals?.cost)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Accumulated Depreciation</Typography>
            <Typography variant="h6">{fmt(data.totals?.accumulatedDepreciation)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Carrying Value</Typography>
            <Typography variant="h6">{fmt(data.totals?.carryingValue)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading && <CircularProgress size={24} />}

      <ScrollTable containerProps={{ component: Paper, variant: 'outlined', sx: { overflowX: 'auto' } }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>Acquired</TableCell>
              <TableCell>Method</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Depreciated</TableCell>
              <TableCell align="right">Carrying</TableCell>
              <TableCell align="right">Next charge</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.assets.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ color: 'text.secondary' }}>
                  No assets registered yet.
                </TableCell>
              </TableRow>
            )}
            {data.assets.map((a) => (
              <TableRow key={a._id}>
                <TableCell>{a.assetNumber}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell>{asDate(a.acquisitionDate)}</TableCell>
                <TableCell>
                  {a.method === 'straight_line'
                    ? `Straight line · ${a.usefulLifeMonths} mo`
                    : `Reducing · ${a.annualRatePercent}%`}
                </TableCell>
                <TableCell align="right">{fmt(a.cost)}</TableCell>
                <TableCell align="right">{fmt(a.accumulatedDepreciation)}</TableCell>
                <TableCell align="right">{fmt(a.carryingValue)}</TableCell>
                <TableCell align="right">{fmt(a.nextMonthlyCharge)}</TableCell>
                <TableCell>
                  <Chip
                    size="small" variant="outlined"
                    label={STATUS_LABEL[a.status] || a.status}
                    color={STATUS_COLOUR[a.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  {a.status !== 'disposed' && (
                    <Button
                      size="small"
                      onClick={() => {
                        setDisposing(a);
                        setDisposal({
                          disposalDate: new Date().toISOString().slice(0, 10),
                          proceeds: 0, proceedsAccountId: '', notes: ''
                        });
                      }}
                    >
                      Dispose
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>

      <Dialog open={showForm} onClose={() => setShowForm(false)} fullWidth maxWidth="md">
        <DialogTitle>Register Fixed Asset</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Acquisition Date" InputLabelProps={{ shrink: true }}
                value={form.acquisitionDate}
                onChange={(e) => setForm({ ...form, acquisitionDate: e.target.value })}
                helperText="Depreciation starts in this month." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Cost" value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Residual Value" value={form.residualValue}
                onChange={(e) => setForm({ ...form, residualValue: e.target.value })}
                helperText="What it will be worth at the end of its life. Depreciation stops here." />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Method" value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}>
                <MenuItem value="straight_line">Straight line</MenuItem>
                <MenuItem value="reducing_balance">Reducing balance</MenuItem>
              </TextField>
            </Grid>
            {form.method === 'straight_line' ? (
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Useful Life (months)"
                  value={form.usefulLifeMonths}
                  onChange={(e) => setForm({ ...form, usefulLifeMonths: e.target.value })} />
              </Grid>
            ) : (
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Annual Rate (%)"
                  value={form.annualRatePercent}
                  onChange={(e) => setForm({ ...form, annualRatePercent: e.target.value })} />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.postAcquisition}
                    onChange={(e) => setForm({ ...form, postAcquisition: e.target.checked })}
                  />
                )}
                label="Post the purchase to the ledger now"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Leave this off if the purchase is already on the books — posting it again
                would count the cost twice. Tick it to record the payment as part of
                registering the asset.
              </Typography>
            </Grid>

            {form.postAcquisition && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Payment Mode" value={form.paymentModeId}
                    onChange={(e) => setForm({ ...form, paymentModeId: e.target.value, paidFromAccountId: '' })}>
                    {modes.map((m) => (
                      <MenuItem key={m._id || m.id} value={m._id || m.id}>{m.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Paid From" value={form.paidFromAccountId}
                    onChange={(e) => setForm({ ...form, paidFromAccountId: e.target.value })}
                    helperText={selectedMode ? `${selectedMode.name} accounts` : 'Choose a mode first'}>
                    {eligibleAccounts.map((a) => (
                      <MenuItem key={a._id || a.id} value={a._id || a.id}>{a.code} — {a.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={working}>Register</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!disposing} onClose={() => setDisposing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Dispose of {disposing?.assetNumber}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Depreciation is charged up to the month of disposal first, then the cost and its
            accumulated depreciation come off the balance sheet and the difference against any
            proceeds is recognised as a gain or loss.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Disposal Date" InputLabelProps={{ shrink: true }}
                value={disposal.disposalDate}
                onChange={(e) => setDisposal({ ...disposal, disposalDate: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Proceeds" value={disposal.proceeds}
                onChange={(e) => setDisposal({ ...disposal, proceeds: e.target.value })}
                helperText="Zero if scrapped." />
            </Grid>
            {Number(disposal.proceeds) > 0 && (
              <Grid item xs={12}>
                <TextField select fullWidth label="Proceeds Received Into"
                  value={disposal.proceedsAccountId}
                  onChange={(e) => setDisposal({ ...disposal, proceedsAccountId: e.target.value })}>
                  {moneyAccounts.map((a) => (
                    <MenuItem key={a._id || a.id} value={a._id || a.id}>{a.code} — {a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={disposal.notes}
                onChange={(e) => setDisposal({ ...disposal, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposing(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={dispose}
            disabled={working || !disposal.disposalDate}>
            Dispose
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssets;
